// Pay-first guest checkout: an anonymous user can pay on Stripe before having
// an account. The Stripe checkout `session_id` is stashed here so it can be
// (a) sent on the subsequent auth request (via getAuthParams) and (b) used to
// fetch the authoritative email on return. localStorage-backed so it survives
// the Google OAuth round-trip. Expires so a stale id never attaches to an
// unrelated future login.

import {readExpiring, writeExpiring} from 'persistence';
import type {GuestCheckoutContext} from 'requests';

const KEY = 'IA__guest_checkout_session';
const TTL_MS = 2 * 60 * 60 * 1000; // 2h

export interface PendingGuestSession extends GuestCheckoutContext {
    sessionId: string;
    /** Whether the checkout being claimed is the 7-day trial rather than a purchase.
     *  Read off the Stripe session by the backend, not from what the client asked for:
     *  it is the login on the far side of the round-trip that has to interpret what the
     *  claim did, and a trial can be refused there (the account may have had one already).
     *  Without it a declined trial and a granted one look the same — an account that isn't
     *  trialing. */
    trial: boolean;
}

export function setPendingGuestSession(sessionId: string, trial = false, context?: GuestCheckoutContext) {
    writeExpiring<PendingGuestSession>(KEY, {sessionId, trial, plan: context?.plan, payment_confirmed: context?.payment_confirmed});
}

export function getPendingGuestSession(): PendingGuestSession | null {
    const stored = readExpiring<PendingGuestSession>(KEY, TTL_MS);
    if (!stored?.sessionId) return null;
    return {sessionId: stored.sessionId, trial: !!stored.trial, plan: stored.plan, payment_confirmed: stored.payment_confirmed};
}

export function consumePendingGuestSession(): PendingGuestSession | null {
    const v = getPendingGuestSession();
    try {
        localStorage.removeItem(KEY);
    } catch {}
    return v;
}
